import os from 'os'

import { memoize } from '@grammarly/function-utils'
import { LoggingImpl, Monitoring, Logging, type LogEvent } from '@grammarly/telemetry'

import { getConfig } from '../../../config/'
import { APP_VERSION, APP_ENV, IS_PRODUCTION } from '../../../constants/globals'
import { transformToString } from '../../utils/error'
import { redactPiiFromUrl } from '../../utils/pii'
import { gnarClient } from '../gnar'
import { stubFetch } from '../metrics/utils'

const { tracking } = getConfig()

export const getLogger: (name: string) => Logging.Logger = memoize(Monitoring.Logging.getLogger)

async function addLoggingContext(event: LogEvent): Promise<LogEvent> {
  const baseContext = {
    ...event.context,
    url: redactPiiFromUrl(document.location.href),
    pathname: document.location.pathname,
  }

  if (event.level >= Logging.LogLevel.WARN) {
    try {
      baseContext.containerId = await gnarClient.getContainerId()
    } catch (error) {
      const errMsg = error instanceof Error ? transformToString(error) : 'unknown'
      baseContext.containerId = `Cannot get containerID: ${errMsg}`
    }
  }

  return event.addContext(baseContext)
}

export function configureLoggerAndConsoleOnClient(name: string): void {
  if (!IS_CLIENT_SIDE) {
    // warn with default logger
    Monitoring.Logging.getLogger('superfunnel.client').warn(
      'Warn: Trying to configure client logging on the server',
    )
    return
  }
  const fetchImpl = IS_PRODUCTION ? fetch.bind(window) : (stubFetch(() => {}) as typeof fetch)
  const felogClient: LoggingImpl.AsyncLogAppender = new LoggingImpl.PostFelogClient(
    tracking.felog.url + '/log',
    name,
    APP_VERSION,
    APP_ENV,
    fetchImpl,
    false,
  )

  const defaultSink: LoggingImpl.LogAppender = async (event: LogEvent): Promise<void> => {
    const eventWithDefaultContext = await addLoggingContext(event)
    if (!IS_PRODUCTION || APP_ENV !== 'prod' || event.level >= Logging.LogLevel.WARN) {
      LoggingImpl.ConsoleLogger.printToConsole(eventWithDefaultContext)
    }
    if (!IS_PRODUCTION || APP_ENV !== 'prod' || event.level >= Logging.LogLevel.INFO) {
      felogClient
        .append(eventWithDefaultContext)
        .catch(error => console.error('Error sending logs', error))
    }
  }

  const logger: Logging.Logger = new LoggingImpl.SimpleLogger(
    name,
    Logging.LogLevel.TRACE,
    defaultSink,
  )

  LoggingImpl.LoggingConfig.configure(logger)
}

export function configureLoggerAndConsoleOnServer(name: string): void {
  if (IS_CLIENT_SIDE) {
    // warn with default logger
    Monitoring.Logging.getLogger('superfunnel.ssr').warn(
      'Warn: Trying to configure server logging on the client',
    )
    return
  }

  const defaultSink: LoggingImpl.LogAppender = (event: LogEvent): void => {
    /**
     * We write to stdout because we use `Log driver: awslogs` for this nodejs server.
     * It means that these logs are going to CloudWatch.
     * Also, we have special lambda-service which takes these logs and put them into our SumoLogic.
     * In SumoLogic you can find them by this query:
     * _sourceCategory=aws/{env}-acquisition-web/cloudwatch-logs AND _sourceHost=/ecs/{env}-cms
     */
    process.stdout.write(JSON.stringify(event) + os.EOL)
  }

  LoggingImpl.LoggingConfig.configure(
    new LoggingImpl.SimpleLogger(name, Logging.LogLevel.INFO, defaultSink),
  )
  Monitoring.Logging.root.context.add({ hostname: os.hostname(), box_id: process.env.BOX_ID })
}
